import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import { useTextStyle } from '@bubble-ui/hook-use-text-style';
import { DesignContextForwardProvider, useDesignContext } from '@klarna/designtokens';
import { useTouchableControl } from '@bubble-ui/provider-touchable-control';
import { useInteractiveStateCombinedWithOverrides } from '@bubble-ui/hook-use-interactive-state';
import { PropsContextProvider } from '@bubble-ui/provider-props-context';
import { isNative } from '@bubble-ui/util-platform';
import { defaultComponentTokens } from './defaultComponentTokens';
import { Link as LinkComponent } from './Link';
var Link = _ref => {
  var {
    'aria-label': ariaLabel,
    addRenderingBox = false,
    ariaLabel: ariaLabelDeprecated,
    children,
    colorToken = 'link/text-color',
    colorTokenArgs,
    direction = 'ltr',
    disabled,
    focused,
    fontFamily,
    fontSize,
    fontWeight,
    hitSlop,
    hovered,
    href,
    id,
    isUnderlined,
    keyboardFocused,
    letterSpacing,
    lineHeight,
    numberOfLines = 0,
    onBlur,
    onFocus,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onPress,
    onPressIn,
    onPressOut,
    onTouchEnd,
    onTouchStart,
    pressed,
    preventLineBreak,
    rel,
    role = 'link',
    tabIndex,
    target,
    testID,
    textAlignToken = 'link/text-alignment',
    textAlignTokenArgs,
    textToken = 'link/text-style',
    textTokenArgs,
    UNSAFE_props
  } = _ref;
  var {
    turnOffTouchability,
    touchablesInteractiveState
  } = useTouchableControl();
  var [interactiveStateCombined, eventHandlers] = useInteractiveStateCombinedWithOverrides({
    disabled,
    focused,
    hovered,
    keyboardFocused,
    onBlur,
    onFocus,
    onPressIn,
    onPressOut,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onTouchEnd,
    onTouchStart,
    pressed
  }, touchablesInteractiveState);
  var designContext = useDesignContext(defaultComponentTokens);
  var isUnderlinedFinal = isUnderlined !== undefined ? isUnderlined : designContext.get('link/text-underlined');
  var textDecorationLine = isUnderlinedFinal ? 'underline' : 'none';
  var textStyle = useTextStyle({
    addRenderingBox,
    colorToken,
    colorTokenArgs: _objectSpread({
      interactiveState: interactiveStateCombined.interactiveState
    }, colorTokenArgs),
    designTokens: defaultComponentTokens,
    direction,
    fontFamily,
    fontSize,
    fontWeight,
    letterSpacing,
    lineHeight,
    numberOfLines,
    textAlignToken,
    textAlignTokenArgs,
    textDecorationLine,
    textToken,
    textTokenArgs
  });
  var nativeAttrs =
  // Ellipsis styling for web is handled by useTextStyle 👆
  isNative ? {
    numberOfLines,
    ellipsizeMode: 'tail'
  } : {};
  var childrenContainRawString = React.Children.toArray(children).some(child => typeof child === 'string');
  return /*#__PURE__*/React.createElement(LinkComponent, _extends({
    childrenContainRawString: childrenContainRawString,
    id: id,
    testID: testID
  }, nativeAttrs, eventHandlers, {
    "aria-label": ariaLabel || ariaLabelDeprecated,
    designContext: designContext,
    disabled: interactiveStateCombined.disabled,
    hitSlop: hitSlop,
    href: href,
    onPress: onPress,
    preventLineBreak: preventLineBreak,
    rel: rel,
    role: role,
    style: _objectSpread(_objectSpread(_objectSpread({}, textStyle), interactiveStateCombined.keyboardFocused && !isNative ? {
      outlineStyle: 'solid',
      outlineWidth: 2,
      outlineColor: designContext.get('link/keyboard-focus-border-color').toString(),
      outlineOffset: 3,
      borderRadius: designContext.get('link/keyboard-focus-corner-radius').value()
    } : {
      outline: 0
    }), interactiveStateCombined.disabled || turnOffTouchability ? {} : {
      cursor: 'pointer'
    }),
    target: target,
    turnOffTouchability: turnOffTouchability,
    tabIndex: tabIndex,
    UNSAFE_props: UNSAFE_props
  }), /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, React.Children.map(children, (child, i) => {
    if (! /*#__PURE__*/React.isValidElement(child) || typeof child !== 'object') {
      return child;
    }
    var childAsElement = child;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: i
    }, /*#__PURE__*/React.createElement(PropsContextProvider, {
      props: {
        Typography: {
          colorToken: childAsElement.props.colorToken || colorToken,
          colorTokenArgs: childAsElement.props.colorTokenArgs || _objectSpread({
            interactiveState: interactiveStateCombined.interactiveState
          }, colorTokenArgs),
          textToken: childAsElement.props.textToken || textToken,
          textTokenArgs: childAsElement.props.textTokenArgs || textTokenArgs,
          textDecorationLine,
          fontFamily: childAsElement.props.fontFamily || fontFamily,
          fontSize: childAsElement.props.fontSize || fontSize,
          fontWeight: childAsElement.props.fontWeight || fontWeight,
          letterSpacing: childAsElement.props.letterSpacing || letterSpacing,
          lineHeight: childAsElement.props.lineHeight || lineHeight
        },
        Icon: {
          colorToken: childAsElement.props.colorToken || colorToken,
          colorTokenArgs: childAsElement.props.colorTokenArgs || _objectSpread({
            interactiveState: interactiveStateCombined.interactiveState
          }, colorTokenArgs)
        },
        ActionIcon: {
          colorToken: childAsElement.props.colorToken || colorToken,
          colorTokenArgs: childAsElement.props.colorTokenArgs || _objectSpread({
            interactiveState: interactiveStateCombined.interactiveState
          }, colorTokenArgs)
        }
      }
    }, child));
  })));
};
Link.displayName = 'Link';
export { Link };
//# sourceMappingURL=index.js.map